import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Gift } from 'lucide-react';
import { setSeoMetadata } from '../../lib/seo';
import { useSiteSettings } from '../../context/SiteSettingsContext';

const TermsConditionsPage = () => {
  // Business name and contact address come from General Settings.
  const { siteName, supportEmail } = useSiteSettings();
  useEffect(() => {
    setSeoMetadata({
      title: 'Terms & Conditions',
      description: 'Our terms of service, engagement and payment policies.',
      keywords: 'terms and conditions, terms of service, policies',
      canonicalPath: '/terms-conditions',
      noIndex: false,
    });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2c1810] to-[#3a1f12] text-white py-16 pt-32">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-[#ffd4b8] hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <FileText className="w-10 h-10 text-[#ff8c42]" />
            <h1 className="text-4xl font-bold">Terms & Conditions</h1>
          </div>
          <p className="text-[#ffd4b8] mt-2">Last updated: October 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-8">
          
          {/* Welcome Notice */}
          <div className="bg-[#fff8f3] border-l-4 border-[#ff8c42] p-6 rounded-r-lg">
            <div className="flex items-start gap-3">
              <Gift className="w-6 h-6 text-[#ff8c42] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-[#2c1810] text-lg">Welcome to {siteName}</h3>
                <p className="text-gray-700 mt-2">
                  Thank you for choosing {siteName} for your software and development work.
                  We take pride in helping businesses automate with reliable, professional-grade equipment.
                  Please review our terms and conditions below.
                </p>
              </div>
            </div>
          </div>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              By accessing and using the {siteName} website and engaging our services, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, you must not use our website or engage our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Products and Services</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {siteName} offers services including but not limited to:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Custom software and web application development</li>
              <li>Website design and development</li>
              <li>Systems integration and automation</li>
              <li>Hosting, maintenance and ongoing support</li>
              <li>Free browser-based tools provided as-is</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Projects and Engagements</h2>
            <p className="text-gray-600 leading-relaxed mb-4">For development work and ongoing services:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Scope, pricing and timelines are set out in a written quote, proposal or statement of work agreed before work begins</li>
              <li>Estimates shown on this website, including any calculator or estimator tool, are indicative only and are not an offer or a quote</li>
              <li>Changes to an agreed scope may affect cost and timeline, and will be confirmed with you before they are carried out</li>
              <li>Where a separate signed agreement exists, that agreement governs if it conflicts with these terms</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Purchaser Requirements</h2>
            <p className="text-gray-600 leading-relaxed mb-4">By making a purchase, you represent and warrant that:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>You are at least 18 years of age or have parental consent</li>
              <li>You have the legal authority to enter into this agreement</li>
              <li>All information provided during checkout is accurate and complete</li>
              <li>You have the right to provide any content, data, credentials or materials you supply to us</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Website and Service Information</h2>
            <p className="text-gray-600 leading-relaxed">
              {siteName} strives to describe its services accurately. While we make every effort to ensure accuracy, we do not warrant that service descriptions, pricing guidance, estimates or other content on our website are error-free, complete, or current. Free tools offered on this website are provided as-is and without warranty.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Pricing and Payment</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              All prices are listed in US dollars and are subject to change without notice. We reserve the right to correct any pricing errors. Payment terms, including any deposit and invoicing schedule, are set out in your quote or agreement.
            </p>
            <p className="text-gray-600 leading-relaxed">
              We accept various payment methods as displayed during checkout. All transactions are processed securely through our authorized payment processors.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Accepting Work</h2>
            <p className="text-gray-600 leading-relaxed">
              We reserve the right to decline or cancel any engagement for any reason, including but not limited to capacity, errors in pricing information, or suspected fraudulent activity. If we cancel before any work has been carried out and payment has already been processed, we will issue a full refund.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Intellectual Property</h2>
            <p className="text-gray-600 leading-relaxed">
              All content on the {siteName} website, including text, graphics, logos, images, and software, is the property of {siteName} or its content suppliers and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-600 leading-relaxed">
              To the maximum extent permitted by law, {siteName} shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our products or website. Our total liability shall not exceed the amount paid for the specific service giving rise to the claim.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Indemnification</h2>
            <p className="text-gray-600 leading-relaxed">
              You agree to indemnify, defend, and hold harmless {siteName} and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including attorney's fees) arising from your use of our services, violation of these terms, or violation of any rights of a third party.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms and Conditions shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes arising under these terms shall be resolved in the appropriate courts.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              {siteName} reserves the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting to our website. Your continued use of the website following any changes constitutes acceptance of the revised terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Information</h2>
            <p className="text-gray-600 leading-relaxed">
              For questions regarding these Terms and Conditions, please contact us:
            </p>
            <div className="mt-4 p-4 bg-[#fff8f3] rounded-lg border border-[#ffe4d4]">
              <p className="font-semibold text-[#2c1810]">{siteName}</p>
              {supportEmail && <p className="text-[#ff8c42]">Email: {supportEmail}</p>}
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsConditionsPage;
